/**
 * When may we ask a signed-in user to turn on browser notifications? Per user, stored in this browser only.
 * "Not now" snoozes: 1st dismissal = 7 days, 2nd = 30 days, 3rd = never ask again (Settings still has the toggle).
 * Browser "Block" is handled by Notification.permission itself ("denied" is never asked again).
 */
const KEY = (userId: string) => `wolfpack_push_prompt_${userId}`;
export const SNOOZE_DAYS: readonly number[] = [7, 30];
export const MAX_DISMISSALS = 3;
const DAY_MS = 86_400_000;

interface PromptState {
  dismissals: number;
  snoozeUntil: number;
}

const read = (userId: string): PromptState => {
  try {
    const raw = localStorage.getItem(KEY(userId));
    const v = raw ? JSON.parse(raw) : null;
    return { dismissals: Number(v?.dismissals) || 0, snoozeUntil: Number(v?.snoozeUntil) || 0 };
  } catch {
    return { dismissals: 0, snoozeUntil: 0 };
  }
};

const write = (userId: string, s: PromptState) => {
  try {
    localStorage.setItem(KEY(userId), JSON.stringify(s));
  } catch {
    /* storage blocked: the prompt may show again next session, which is acceptable */
  }
};

export function canShowPushPrompt(userId: string | undefined | null, now: number = Date.now()): boolean {
  if (!userId) return false;
  const s = read(userId);
  return s.dismissals < MAX_DISMISSALS && now >= s.snoozeUntil;
}

export function recordPushPromptDismissed(userId: string | undefined | null, now: number = Date.now()): void {
  if (!userId) return;
  const dismissals = read(userId).dismissals + 1;
  const days = SNOOZE_DAYS[dismissals - 1];
  write(userId, { dismissals, snoozeUntil: days ? now + days * DAY_MS : Number.MAX_SAFE_INTEGER });
}

export function clearPushPromptState(userId: string | undefined | null): void {
  if (!userId) return;
  try {
    localStorage.removeItem(KEY(userId));
  } catch {
    /* ignore */
  }
}
